// AuthContext.js
import React, { createContext, useState, useContext, useEffect } from "react";
import { isAuthenticated, getToken, login as loginService, logout as logoutService } from "../services/authService";
import { getAllGrids } from "../services/gridService";
import { getAllSensorPacks } from "../services/sensorPackService";
import { getUserProfile } from "../services/userService";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // User profile state
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const [userError, setUserError] = useState(null);

  // Grids state
  const [grids, setGrids] = useState([]);
  const [gridsLoading, setGridsLoading] = useState(false);
  const [gridsError, setGridsError] = useState(null);

  // Sensor packs state (for admins)
  const [sensorPacks, setSensorPacks] = useState([]);
  const [sensorPacksLoading, setSensorPacksLoading] = useState(false);
  const [sensorPacksError, setSensorPacksError] = useState(null);

  // Demo mode (admin-only) — anonymizes grid names/locations so the dashboard
  // can be previewed with prospective customers. Persisted across reloads.
  // Every consumer must still gate it behind is_admin; this only holds the flag.
  const [demoMode, setDemoMode] = useState(() => {
    try { return localStorage.getItem('subtidal.demoMode') === '1'; }
    catch { return false; }
  });
  const toggleDemoMode = () => {
    setDemoMode(prev => {
      const next = !prev;
      try { localStorage.setItem('subtidal.demoMode', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  // Helper function to get pond grids
  const getPondGrids = () => {
    return grids.filter(grid =>
      grid.name.toLowerCase().includes('pond')
    );
  };

  // Function to fetch all user data after authentication
  const fetchUserData = async (authToken) => {
    try {
      // Fetch user profile
      setUserLoading(true);
      setUserError(null);
      const userProfile = await getUserProfile(authToken);
      setUser(userProfile);

      // Fetch grids
      setGridsLoading(true);
      setGridsError(null);
      const gridsResponse = await getAllGrids(authToken);
      setGrids(gridsResponse.results || []);

      // Fetch sensor packs if user is admin
      if (userProfile.is_admin) {
        setSensorPacksLoading(true);
        setSensorPacksError(null);
        try {
          const sensorPacksResponse = await getAllSensorPacks(authToken);
          setSensorPacks(sensorPacksResponse.results || sensorPacksResponse || []);
        } catch (error) {
          console.error('Error fetching sensor packs:', error);
          setSensorPacksError(error.message);
        } finally {
          setSensorPacksLoading(false);
        }
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      if (error.message.includes('user profile')) {
        setUserError(error.message);
      }
      if (error.message.includes('grids')) {
        setGridsError(error.message);
      }
    } finally {
      setUserLoading(false);
      setGridsLoading(false);
    }
  };

  // Function to clear all user data
  const clearUserData = () => {
    setUser(null);
    setGrids([]);
    setSensorPacks([]);
    setUserError(null);
    setGridsError(null);
    setSensorPacksError(null);
  };

  // Check if user is logged in on initial load
  useEffect(() => {
    const checkAuthStatus = async () => {
      const authStatus = isAuthenticated();
      setIsLoggedIn(authStatus);

      if (authStatus) {
        const authToken = getToken();
        setToken(authToken);

        // Fetch all user data if authenticated
        if (authToken) {
          await fetchUserData(authToken);
        }
      }

      setLoading(false);
    };

    checkAuthStatus();
  }, []);

  // Login function
  const login = async (username, password) => {
    try {
      const data = await loginService(username, password);
      setIsLoggedIn(true);
      setToken(data.token);

      // Fetch all user data after successful login
      await fetchUserData(data.token);

      return data;
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  // Logout function
  const logout = () => {
    logoutService();
    setIsLoggedIn(false);
    setToken(null);
    clearUserData();
  };

  // Function to refresh user data (useful for manual refresh)
  const refreshUserData = async () => {
    if (token) {
      await fetchUserData(token);
    }
  };

  const value = {
    // Auth state
    isLoggedIn,
    token,
    login,
    logout,
    loading,

    // User profile data
    user,
    userLoading,
    userError,

    // Grids data
    grids,
    gridsLoading,
    gridsError,
    getPondGrids,

    // Sensor packs data (for admins)
    sensorPacks,
    sensorPacksLoading,
    sensorPacksError,

    // Demo mode (admin-only)
    demoMode,
    toggleDemoMode,

    // Utility functions
    refreshUserData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};